import {
  EMPTY_ARRAY,
  formattedDateForSubscription,
  request,
  toBigWithDefault,
  useEndpointsContext,
  type IOrderConfigurationSnapshot,
  type IOrderTrajectory,
  type MinimalSubscriptionResponse,
  type OrderAnalytic,
} from '@talos/kyoko';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { toMillis, type Millis, type TrajectoryOrder } from './trajectoryOrder';
import { canPredTwapBounds, genTwapBounds, shouldInvalidateTwapBounds } from './twapBounds';

export interface PredictedBoundsPoint {
  ts: Millis;
  target: number;
  lb: number;
  ub: number;
}

interface UsePredictionsReturnType {
  canPredict: boolean;
  bounds: PredictedBoundsPoint[];
  shouldInvalidate: (analytics: OrderAnalytic[]) => boolean;
  isLoading: boolean;
  error: Error | null;
}

const DONT_INVALIDATE = (_: OrderAnalytic[]) => false;
const BOUNDS_DISABLED: UsePredictionsReturnType = {
  canPredict: false,
  bounds: EMPTY_ARRAY,
  shouldInvalidate: DONT_INVALIDATE,
  isLoading: false,
  error: null,
};
const BOUNDS_NO_RESULT: UsePredictionsReturnType = {
  canPredict: false,
  bounds: EMPTY_ARRAY,
  shouldInvalidate: DONT_INVALIDATE,
  isLoading: false,
  error: new Error("Can't compute trajectory bound predictions for this order."),
};
const TWAP_BOUNDS_STATE_ERROR = new Error('Trajectory bound predictions are state/invalid.');

const ACQUISITION_LIMIT_STATUS_ID = 'ORDER_TRAJECTORY';
const generateTrajectoryServiceQueryID = (order: TrajectoryOrder) => {
  return [ACQUISITION_LIMIT_STATUS_ID, '/', order];
};

function parseBoundsFromTrajectoryService(resp: MinimalSubscriptionResponse<IOrderTrajectory>): PredictedBoundsPoint[] {
  const bins = resp.data?.[0].Bins ?? [];

  return bins.map(bin => ({
    ts: toMillis(bin.StartTime),
    lb: toBigWithDefault(bin.StartLowerBound, 0).toNumber(),
    ub: toBigWithDefault(bin.StartUpperBound, 0).toNumber(),
    target: toBigWithDefault(bin.StartUpperBound, 0).toNumber(),
  }));
}

function useTrajectoryServiceBoundsQuery(enabled: boolean, order: TrajectoryOrder) {
  const { orgApiEndpoint } = useEndpointsContext();

  return useQuery<PredictedBoundsPoint[]>({
    enabled: !!orgApiEndpoint && enabled,
    queryKey: generateTrajectoryServiceQueryID(order),
    queryFn: async () =>
      request<MinimalSubscriptionResponse<IOrderTrajectory>>('POST', `${orgApiEndpoint}/order-trajectory`, {
        StartTime: formattedDateForSubscription(order.startTime),
        EndTime: formattedDateForSubscription(order.endTime),
        Symbol: order.symbol,
        BoundsControl: order.boundsControl,
        Quantity: order.orderQty,
        Strategy: order.strategy,
        Markets: order.markets,
      }).then(response => {
        return parseBoundsFromTrajectoryService(response);
      }),
  });
}

function useTwapBounds(enabled: boolean, order: TrajectoryOrder, configSnapshot?: IOrderConfigurationSnapshot) {
  const { schedule: bounds } = useMemo(() => {
    if (!enabled || !configSnapshot) {
      return { schedule: [] };
    }
    return genTwapBounds(order, configSnapshot);
  }, [enabled, order, configSnapshot]);

  return bounds;
}

interface UseBoundPredictionsSharedParams {
  enable: boolean;
  order: TrajectoryOrder;
}

interface UseBoundPredictionsDynamicParams extends UseBoundPredictionsSharedParams {
  configSnapshot: IOrderConfigurationSnapshot;
  staticBounds?: undefined;
}

interface UseBoundPredictionsStaticParams extends UseBoundPredictionsSharedParams {
  /** Pre-computed bounds (e.g., from PreTrade). When provided, bypasses dynamic prediction calculation. */
  staticBounds: PredictedBoundsPoint[];
  configSnapshot?: never;
}

type UseBoundPredictionsParams = UseBoundPredictionsDynamicParams | UseBoundPredictionsStaticParams;

const STATIC_BOUNDS_RESULT = (bounds: PredictedBoundsPoint[]): UsePredictionsReturnType => ({
  canPredict: bounds.length > 0,
  bounds,
  shouldInvalidate: DONT_INVALIDATE,
  isLoading: false,
  error: null,
});

export function useBoundPredictions(params: UseBoundPredictionsParams): UsePredictionsReturnType {
  const { enable, order } = params;
  const configSnapshot = 'configSnapshot' in params ? params.configSnapshot : undefined;
  const staticBounds = 'staticBounds' in params ? params.staticBounds : undefined;

  // When static bounds are provided, skip dynamic prediction calculation
  const hasStaticBounds = staticBounds != null;
  const enableTrajectoryBounds = !hasStaticBounds && order.canBackendCalcTrajectory && enable;
  const enableTwapBounds = !hasStaticBounds && configSnapshot != null && canPredTwapBounds(order, configSnapshot);

  const trajectoryBoundsQuery = useTrajectoryServiceBoundsQuery(enableTrajectoryBounds, order);
  const twapBounds = useTwapBounds(enableTwapBounds, order, configSnapshot);

  const [twapError, setTwapError] = useState<Error | null>(null);
  const checkShouldInvalidateTwap = useCallback(
    (analytics: OrderAnalytic[]) => {
      const shouldInvalidate = shouldInvalidateTwapBounds(order.orderQty, analytics);
      if (shouldInvalidate) {
        setTwapError(TWAP_BOUNDS_STATE_ERROR);
      }
      return shouldInvalidate;
    },
    [order.orderQty]
  );

  // Static bounds take precedence
  if (hasStaticBounds) {
    return STATIC_BOUNDS_RESULT(staticBounds);
  }

  if (!enable) {
    return BOUNDS_DISABLED;
  }

  if (enableTrajectoryBounds) {
    const { data: bounds, isLoading, error } = trajectoryBoundsQuery;

    return {
      canPredict: true,
      bounds: bounds ?? [],
      shouldInvalidate: DONT_INVALIDATE,
      isLoading,
      error,
    };
  }

  if (twapBounds) {
    return {
      canPredict: true,
      bounds: twapBounds,
      shouldInvalidate: checkShouldInvalidateTwap,
      isLoading: false,
      error: twapError,
    };
  }

  return BOUNDS_NO_RESULT;
}
